import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useSearchCardsQuery } from 'store/services/cards/search/cardsSearchApi';
import { cardImageInputsFromCard, getCardImageUrl } from 'utils/cardImageFallback';
import { buildCardUrl } from 'utils/cardUrlUtils';
import Points from 'components/Points';
import { SearchFilterState } from '../searchState';
import { applySyntax } from '../syntaxParser';
import './SearchTypeahead.css';

const TYPEAHEAD_DEBOUNCE_MS = 200;
const MAX_SUGGESTIONS = 5;

interface SearchTypeaheadProps {
  value: string;
  onChange: (value: string) => void;
  state: SearchFilterState;
  onApplyState: (state: SearchFilterState) => void;
  recentSearches: string[];
}

export const SearchTypeahead: React.FC<SearchTypeaheadProps> = ({
  value,
  onChange,
  state,
  onApplyState,
  recentSearches,
}) => {
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [syntaxMode, setSyntaxMode] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const debouncedValue = useDebouncedValue(value, TYPEAHEAD_DEBOUNCE_MS);
  const query = debouncedValue.trim();

  const { data, isFetching } = useSearchCardsQuery(
    { name: query, limit: MAX_SUGGESTIONS, page: 1 },
    { skip: syntaxMode || query.length < 2 },
  );

  const suggestions = useMemo(() => data?.cards?.slice(0, MAX_SUGGESTIONS) ?? [], [data]);
  const showSuggestions = isFocused && !syntaxMode && query.length >= 2 && suggestions.length > 0;
  const showRecent = isFocused && !syntaxMode && value.trim() === '' && recentSearches.length > 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setIsFocused(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const openCard = useCallback(
    (index: number) => {
      const card = suggestions[index];
      if (!card) return;
      const scryfallID = (card as unknown as { scryfallId?: string }).scryfallId ?? card.scryfallID;
      navigate(
        buildCardUrl({
          name: card.name,
          setCode: card.setCode,
          collectorNumber: card.collectorNumber ?? undefined,
          scryfallID,
        }),
      );
    },
    [suggestions, navigate],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsFocused(false);
      return;
    }
    if (syntaxMode && event.key === 'Enter') {
      onApplyState(applySyntax(state, value));
      setSyntaxMode(false);
      return;
    }
    if (!showSuggestions) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      openCard(activeIndex);
    }
  };

  return (
    <div className="search-typeahead" ref={wrapperRef}>
      <div className="search-typeahead-field">
        <input
          type="text"
          className="search-typeahead-input"
          value={value}
          placeholder={syntaxMode ? 'e.g. t:instant mv<=2 c:u' : 'Search cards by name'}
          aria-label="Search cards"
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls="search-typeahead-list"
          aria-autocomplete="list"
          aria-activedescendant={
            showSuggestions && activeIndex >= 0 ? `search-suggestion-${activeIndex}` : undefined
          }
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className={`search-syntax-chip ${syntaxMode ? 'active' : ''}`}
          aria-pressed={syntaxMode}
          title="Type a compact query, e.g. t:instant mv<=2"
          onClick={() => setSyntaxMode((m) => !m)}
        >
          syntax
        </button>
      </div>

      {syntaxMode && (
        <p className="search-syntax-hint">
          Press Enter to apply. Unrecognized words become the card name.
        </p>
      )}

      {showRecent && (
        <div className="search-recent">
          <span className="search-recent-label">Recent</span>
          {recentSearches.map((term) => (
            <button
              key={term}
              type="button"
              className="search-recent-chip"
              onClick={() => onChange(term)}
            >
              {term}
            </button>
          ))}
        </div>
      )}

      {showSuggestions && (
        <ul className="search-suggestions" id="search-typeahead-list" role="listbox">
          {suggestions.map((card, index) => {
            const scryfallId =
              (card as unknown as { scryfallId?: string }).scryfallId ?? card.scryfallID;
            const points = (card as unknown as { pointValue?: number | null }).pointValue;
            const offeredCount = card.holderCount;
            return (
              <li
                key={`${scryfallId}-${index}`}
                id={`search-suggestion-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`search-suggestion ${index === activeIndex ? 'active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                // mousedown, not click: the input's blur would otherwise close
                // the list before the click lands.
                onMouseDown={(e) => {
                  e.preventDefault();
                  openCard(index);
                }}
              >
                <img
                  className="search-suggestion-art"
                  src={getCardImageUrl(cardImageInputsFromCard(card), 'small')}
                  alt=""
                  aria-hidden="true"
                />
                <span className="search-suggestion-text">
                  <span className="search-suggestion-name">{card.name}</span>
                  <span className="search-suggestion-type">{card.typeLine ?? card.setName}</span>
                </span>
                <span className="search-suggestion-meta">
                  {points != null ? <Points points={points} type="small" /> : <span className="search-suggestion-unpriced">???</span>}
                  {/* "offering" is invariant, so no singular/plural branch. */}
                  {offeredCount !== undefined && (
                    <span className="search-suggestion-holders">{offeredCount} offering</span>
                  )}
                </span>
              </li>
            );
          })}
          {isFetching && <li className="search-suggestion-loading">Searching...</li>}
        </ul>
      )}
    </div>
  );
};

export default SearchTypeahead;
